import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import * as api from '@/features/backlog/api';
import { Board, Item, Member, Status } from '@/features/backlog/types';
import { useAppData } from '@/app-data';
import { CanvasElement } from '@/features/canvas/types';

/**
 * The whiteboard's backlog-item store for item-cards (SBB-13). It loads ALL items for the boards its
 * cards reference (exactly like the kanban page loads a board), so subtask trees (parent_id) and epic
 * member lists (epic_id) can be derived client-side, and keeps them LIVE: item mutations made anywhere
 * (kanban, table, drawer, another whiteboard) are fanned into this whiteboard's realtime room and
 * applied via applyItemUpsert/applyItemDelete. Edits made ON a card write through saveItem/createItem.
 */
export interface CanvasItemsStore {
  itemsById: Map<string, Item>;
  childrenBy: Map<string, Item[]>; // parent_id → children, ordered
  membersByEpic: Map<string, Item[]>; // epic_id → member items, ordered
  statusesByBoard: Map<string, Status[]>;
  members: Member[];
  boardsById: Map<string, Board>;
  loading: boolean;
  applyItemUpsert: (it: Item) => void;
  applyItemDelete: (ids: string[]) => void;
  saveItem: (id: string, patch: Partial<Item>) => void;
  createItemOnBoard: (boardId: string, fields: Partial<Item>) => Promise<Item | null>;
  /** Refetch every referenced board's items — called after a realtime reconnect to catch missed events. */
  reload: () => void;
  /** Increments on every applied realtime item change — search cards re-run their query off it. */
  changeTick: number;
}

const orderBy = (arr: Item[]) => arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

export function useCanvasItemsStore(elements: CanvasElement[]): CanvasItemsStore {
  const { members, boards } = useAppData();
  const boardsById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [itemsById, setItemsById] = useState<Map<string, Item>>(new Map());
  const [statusesByBoard, setStatusesByBoard] = useState<Map<string, Status[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [changeTick, setChangeTick] = useState(0);
  const loadedBoards = useRef<Set<string>>(new Set());

  // The distinct boards this whiteboard's item-cards reference (cards cache their item's board_id).
  const referencedBoardIds = useMemo(() => {
    const s = new Set<string>();
    elements.forEach((el) => {
      if (el.kind === 'item' && el.style?.board_id) s.add(el.style.board_id);
    });
    return Array.from(s);
  }, [elements]);

  // Load items + statuses for any newly-referenced board (once each).
  useEffect(() => {
    const toLoad = referencedBoardIds.filter((b) => !loadedBoards.current.has(b));
    if (!toLoad.length) return;
    toLoad.forEach((b) => loadedBoards.current.add(b));
    setLoading(true);
    Promise.all(
      toLoad.map(async (boardId) => {
        const [res, sts] = await Promise.all([
          api.listItems({ limit: 2000, filter: [{ name: 'board_id', op: 'eq', value: boardId }] }).catch(() => ({ data: [] as Item[], total: 0 })),
          api.listStatuses(boardId).catch(() => [] as Status[]),
        ]);
        return { boardId, items: res.data, statuses: sts };
      }),
    )
      .then((results) => {
        setItemsById((prev) => {
          const next = new Map(prev);
          results.forEach((r) => r.items.forEach((it) => next.set(it.id, it)));
          return next;
        });
        setStatusesByBoard((prev) => {
          const next = new Map(prev);
          results.forEach((r) => next.set(r.boardId, r.statuses));
          return next;
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencedBoardIds, reloadNonce]);

  const reload = useCallback(() => {
    loadedBoards.current.clear();
    setReloadNonce((n) => n + 1);
  }, []);

  const applyItemUpsert = useCallback((it: Item) => {
    setItemsById((prev) => {
      const n = new Map(prev);
      n.set(it.id, it);
      return n;
    });
    setChangeTick((t) => t + 1);
  }, []);
  const applyItemDelete = useCallback((ids: string[]) => {
    setItemsById((prev) => {
      const n = new Map(prev);
      ids.forEach((id) => n.delete(id));
      return n;
    });
    setChangeTick((t) => t + 1);
  }, []);

  const childrenBy = useMemo(() => {
    const m = new Map<string, Item[]>();
    itemsById.forEach((it) => {
      if (it.parent_id) {
        const arr = m.get(it.parent_id) ?? [];
        arr.push(it);
        m.set(it.parent_id, arr);
      }
    });
    m.forEach(orderBy);
    return m;
  }, [itemsById]);

  const membersByEpic = useMemo(() => {
    const m = new Map<string, Item[]>();
    itemsById.forEach((it) => {
      if (it.epic_id) {
        const arr = m.get(it.epic_id) ?? [];
        arr.push(it);
        m.set(it.epic_id, arr);
      }
    });
    m.forEach(orderBy);
    return m;
  }, [itemsById]);

  const applyItem = useCallback((it: Item) => {
    setItemsById((prev) => {
      const n = new Map(prev);
      n.set(it.id, it);
      return n;
    });
  }, []);

  const saveItem = useCallback(
    (id: string, patch: Partial<Item>) => {
      setItemsById((prev) => {
        const cur = prev.get(id);
        if (!cur) return prev;
        const n = new Map(prev);
        n.set(id, { ...cur, ...patch });
        return n;
      });
      // On failure (offline, validation, deleted), re-fetch the server truth so the card can't keep a
      // phantom, non-persisted edit. (A successful write echoes back via realtime, updating everyone.)
      api.updateItem(id, patch).catch(() => {
        api.getItem(id).then(applyItem).catch(() => undefined);
      });
    },
    [applyItem],
  );

  const createItemOnBoard = useCallback(
    async (boardId: string, fields: Partial<Item>): Promise<Item | null> => {
      try {
        const it = await api.createItem({ board_id: boardId, ...fields });
        // Don't mark the board fully-loaded here (we only have this one item) — placing the card enters
        // its board into referencedBoardIds and the load effect fetches the board's FULL item set.
        applyItem(it);
        return it;
      } catch {
        return null;
      }
    },
    [applyItem],
  );

  return { itemsById, childrenBy, membersByEpic, statusesByBoard, members, boardsById, loading, applyItemUpsert, applyItemDelete, saveItem, createItemOnBoard, reload , changeTick };
}

const CanvasItemsContext = createContext<CanvasItemsStore | null>(null);
export const CanvasItemsProvider = CanvasItemsContext.Provider;
export const useCanvasItems = (): CanvasItemsStore => {
  const v = useContext(CanvasItemsContext);
  if (!v) throw new Error('useCanvasItems must be used within a CanvasItemsProvider');
  return v;
};
